/**
 * BudgetTab.jsx - Tracking > Setup > Budget: the won Worksheet quote's worksheet
 * (engineers, days, tasks, hours, per-day expenses), editable by whoever has
 * Projects Edit, plus change orders / FCOs that add to it. The same editor as
 * the quote (components/worksheet); totals are worked out by the server as
 * you type (POST .../budget/preview) and saved with Save budget.
 * Cost, COGS and margin only for the 'cost-rates' area; "Apply to the Quote
 * tab" (that area too) writes the budget's cost onto the record's Quoted
 * Labor Cost / Quoted Expenses - on purpose a button, because those fields
 * are seen by everyone who sees the project.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import WorksheetEditor from '../worksheet/WorksheetEditor';
import { Btn, GREEN, MUTED, NumInput, Section, SelectInput, TextInput, td, th } from '../quotes/quoteUi';
import { applyBudgetToQuoteFields, budgetFromQuote, changeFromQuote, hours, money, pct, previewBudget, saveBudget } from '../costs/costApi';

const blankBudget = (customer) => ({
  worksheet: { customer: customer || '', start: '', days: 161, billable: {}, nonBillable: {}, engineers: [] },
  changes: [],
});
const newChange = () => ({ id: `co-${Date.now().toString(36)}`, ref: '', date: '', description: '', hours: '', sell: '', expenses: '' });

function Card({ label, value, tone }) {
  return (
    <div style={{ background: tone || '#F6F8F3', borderRadius: 8, padding: '8px 12px', minWidth: 120 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#1F2328', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

export default function BudgetTab({ data, projectId, editable, seesCosts, setData, onDirty }) {
  const saved = data.budget;
  const [draft, setDraft] = useState(() => (saved ? saved.data : null));
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const quotes = data.worksheetQuotes || [];
  const [rev, setRev] = useState(() => {
    const q = quotes.filter((x) => !x.fco);
    return q.length ? String(q[q.length - 1].revision) : quotes.length ? String(quotes[quotes.length - 1].revision) : '';
  });
  const [coRev, setCoRev] = useState('');
  useEffect(() => {
    setDraft(saved ? saved.data : null);
    setPreview(null);
  }, [saved]);
  const dirty = useMemo(() => !!draft && JSON.stringify(draft) !== JSON.stringify(saved ? saved.data : null), [draft, saved]);
  useEffect(() => {
    if (onDirty) onDirty(dirty);
  }, [dirty, onDirty]);

  // live totals while typing
  const seq = useRef(0);
  useEffect(() => {
    if (!dirty || !editable) return undefined;
    const n = ++seq.current;
    const t = setTimeout(async () => {
      const r = await previewBudget(projectId, draft);
      if (n === seq.current && r.ok) setPreview(r.body.budget);
    }, 400);
    return () => clearTimeout(t);
  }, [draft, dirty, editable, projectId]);

  const run = async (fn, okText) => {
    setBusy(true);
    const r = await fn();
    setBusy(false);
    if (!r.ok) {
      setMsg({ kind: 'error', text: (r.body && r.body.error) || 'That did not work.' });
      return null;
    }
    setMsg(okText ? { kind: 'ok', text: okText } : null);
    if (r.body && r.body.project) setData(r.body);
    return r.body;
  };
  const shown = preview || saved;
  const T = shown && shown.totals;

  if (!draft) {
    return (
      <div>
        <Section
          title="Budget"
          hint="The won quote's worksheet becomes this project's budget: hours by engineer, task and day, and the expenses. Change orders add to it."
        >
          {msg && (
            <div role="alert" style={{ color: '#B71C1C', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              {msg.text}
            </div>
          )}
          <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
            No budget yet. Without one the Summary cannot say how much of the job is used or when the budget runs out.
          </div>
          {editable && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {quotes.length > 0 && (
                <>
                  <SelectInput
                    aria-label="Quote revision"
                    value={rev}
                    style={{ width: 220 }}
                    onChange={setRev}
                    options={quotes.map((q) => ({
                      value: String(q.revision),
                      label: `${q.offerNo || 'Worksheet quote'} R${q.revision}${q.fco ? ' (FCO)' : ''}`,
                    }))}
                  />
                  <Btn
                    primary
                    disabled={busy}
                    onClick={() => run(() => budgetFromQuote(projectId, Number(rev)), "The budget is the quote's worksheet now.")}
                  >
                    Take the budget from this quote
                  </Btn>
                </>
              )}
              <Btn disabled={busy} onClick={() => setDraft(blankBudget(data.rateCustomer))}>
                Start a blank budget
              </Btn>
              {!quotes.length && (
                <span style={{ fontSize: 11, color: MUTED }}>This project has no Worksheet quote (Build quote &gt; Worksheet quote).</span>
              )}
            </div>
          )}
        </Section>
      </div>
    );
  }

  const changes = draft.changes || [];
  const calcChanges = (shown && shown.changes) || [];
  const setChange = (i, k, v) => setDraft((d) => ({ ...d, changes: d.changes.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const save = () =>
    run(() => saveBudget(projectId, draft, saved && saved.updatedAt), 'Budget saved.').then((b) => {
      if (b) setPreview(null);
    });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {T && <Card label="Budget hours" value={hours(T.hours)} />}
        {T && <Card label="Budget sold" value={money(T.sold)} tone="#E8F5E9" />}
        {T && seesCosts && <Card label="Budget cost" value={money(T.cost)} tone="#F3E5F5" />}
        {T && seesCosts && <Card label="Budget margin" value={pct(T.margin)} tone="#F3E5F5" />}
        <span style={{ fontSize: 11, color: MUTED }}>
          {saved ? `From ${saved.source || 'typed'}; saved by ${saved.updatedBy || '-'}` : 'Not saved yet'}
          {preview ? ' · showing unsaved changes' : ''}
        </span>
        {editable && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {dirty && (
              <Btn
                small
                disabled={busy}
                onClick={() => {
                  setDraft(saved ? saved.data : null);
                  setPreview(null);
                }}
              >
                Undo changes
              </Btn>
            )}
            <Btn primary disabled={!dirty || busy} onClick={save}>
              {busy ? 'Saving...' : 'Save budget'}
            </Btn>
            {seesCosts && saved && (
              <Btn
                small
                disabled={busy}
                title="Write the budget onto the Quote tab: Quoted Price, Quoted Labor Cost, Quoted Expenses - those fields are seen by everyone who sees the project"
                onClick={async () => {
                  // Sharif (10/12/26): it overwrites a Quoted Price already there - after asking
                  if (
                    !window.confirm(
                      `Write the budget onto this project's Quote tab?\n\nThe Quoted Price is REPLACED by the budget's ${money(saved.totals.sold)}, and Quoted Labor Cost / Quoted Expenses by the budget's cost. Everyone who can see the project sees those fields.`
                    )
                  )
                    return;
                  const b = await run(() => applyBudgetToQuoteFields(projectId));
                  if (b)
                    setMsg({
                      kind: 'ok',
                      text: `The Quote tab now carries the budget: Quoted Price ${money(b.previousQuotedPrice)} -> ${money(b.quotedPrice)}. Reopen the project to see it.`,
                    });
                }}
              >
                Apply to the Quote tab
              </Btn>
            )}
          </span>
        )}
      </div>
      {msg && (
        <div
          role={msg.kind === 'error' ? 'alert' : 'status'}
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: msg.kind === 'error' ? '#FFEBEE' : '#E8F5E9',
            color: msg.kind === 'error' ? '#B71C1C' : GREEN,
          }}
        >
          {msg.text}
        </div>
      )}

      <WorksheetEditor
        ws={draft.worksheet}
        setWs={(fn) => setDraft((d) => ({ ...d, worksheet: typeof fn === 'function' ? fn(d.worksheet) : fn }))}
        settings={data.card || {}}
        staff={data.staffDirectory || []}
        calc={shown ? shown.calc : null}
        seesCosts={seesCosts}
        canOverride={false}
        readOnly={!editable}
      />

      <Section
        title="Change orders and FCOs"
        hint="What was added to the job after the quote. A change from a Worksheet quote revision carries its own cost; a typed one is costed at the budget's cost per hour plus its expenses."
        right={
          editable && (
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {quotes.length > 0 && (
                <>
                  <SelectInput
                    aria-label="Change order quote"
                    value={coRev}
                    blank="From a quote revision..."
                    style={{ width: 210 }}
                    onChange={setCoRev}
                    options={quotes.map((q) => ({
                      value: String(q.revision),
                      label: `${q.offerNo || 'Worksheet quote'} R${q.revision}${q.fco ? ' (FCO)' : ''}`,
                    }))}
                  />
                  <Btn
                    small
                    disabled={!coRev || busy || dirty}
                    title={dirty ? 'Save the budget first' : ''}
                    onClick={() => run(() => changeFromQuote(projectId, Number(coRev)), 'Change order added.')}
                  >
                    Add
                  </Btn>
                </>
              )}
              <Btn small onClick={() => setDraft((d) => ({ ...d, changes: [...(d.changes || []), newChange()] }))}>
                + Typed change
              </Btn>
            </span>
          )
        }
      >
        {changes.length === 0 ? (
          <div style={{ fontSize: 11.5, color: MUTED }}>No change orders.</div>
        ) : (
          <table aria-label="Change orders" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Ref', 'Date', 'Description', 'Hours', 'Sold ($)', 'of which expenses ($)', ...(seesCosts ? ['Cost'] : []), ''].map(
                  (x) => (
                    <th key={x} style={th}>
                      {x}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => {
                const k = calcChanges.find((x) => x.id === c.id);
                return (
                  <tr key={c.id || i}>
                    <td style={{ ...td, width: 150 }}>
                      <TextInput
                        aria-label={`Change ${i + 1} ref`}
                        value={c.ref}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'ref', v)}
                      />
                    </td>
                    <td style={{ ...td, width: 140 }}>
                      <TextInput
                        type="date"
                        aria-label={`Change ${i + 1} date`}
                        value={c.date}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'date', v)}
                      />
                    </td>
                    <td style={td}>
                      <TextInput
                        aria-label={`Change ${i + 1} description`}
                        value={c.description}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'description', v)}
                      />
                    </td>
                    <td style={{ ...td, width: 80 }}>
                      <NumInput
                        aria-label={`Change ${i + 1} hours`}
                        value={c.hours}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'hours', v)}
                      />
                    </td>
                    <td style={{ ...td, width: 110 }}>
                      <NumInput
                        aria-label={`Change ${i + 1} sold`}
                        value={c.sell}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'sell', v)}
                      />
                    </td>
                    <td style={{ ...td, width: 110 }}>
                      <NumInput
                        aria-label={`Change ${i + 1} expenses`}
                        value={c.expenses}
                        disabled={!editable}
                        onChange={(v) => setChange(i, 'expenses', v)}
                      />
                    </td>
                    {seesCosts && (
                      <td style={{ ...td, width: 110, fontSize: 11.5, textAlign: 'right' }}>
                        {k ? `${money(k.cost)}${k.costEstimated ? ' (est.)' : ''}` : ''}
                      </td>
                    )}
                    <td style={{ ...td, width: 30 }}>
                      {editable && (
                        <Btn
                          small
                          danger
                          aria-label={`Remove change ${i + 1}`}
                          onClick={() => setDraft((d) => ({ ...d, changes: d.changes.filter((_, j) => j !== i) }))}
                        >
                          x
                        </Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {!shown && data.trackingSheet && data.trackingSheet.quoted && (
        <Section
          title="Quoted parameters (from the tracking sheet)"
          hint={`No budget yet: the Summary compares the actuals with the MASTER SUMMARY's quoted hours and expenses of ${data.trackingSheet.file.split('/').pop()} (Rev ${data.trackingSheet.revision}).`}
        >
          <table aria-label="Quoted parameters from the tracking sheet" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['NT', 'OT', 'DT', 'Airfare', 'Hotel', 'Car/Taxi', 'Meals', 'Misc', 'Other'].map((x) => (
                  <th key={x} style={th}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {['nt', 'ot', 'dt'].map((k) => (
                  <td key={k} style={{ ...td, fontSize: 11.5, textAlign: 'right' }}>
                    {hours(data.trackingSheet.quoted[k])}
                  </td>
                ))}
                {['airfare', 'hotel', 'car', 'meals', 'misc', 'other'].map((k) => (
                  <td key={k} style={{ ...td, fontSize: 11.5, textAlign: 'right' }}>
                    {money(data.trackingSheet.quoted[k])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Section>
      )}

      {shown && shown.quoted && (
        <Section
          title="Quoted parameters (for the tracking sheet)"
          hint="What the Summary compares the actuals with: the budget's hours and its billable expenses in the tracking sheet's categories, and what it sells for."
        >
          <table aria-label="Quoted parameters" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['NT', 'OT', 'DT', 'Airfare', 'Hotel', 'Car/Taxi', 'Meals', 'Misc', 'Other', 'Sold'].map((x) => (
                  <th key={x} style={th}>
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {['nt', 'ot', 'dt'].map((k) => (
                  <td key={k} style={{ ...td, fontSize: 11.5, textAlign: 'right' }}>
                    {hours(shown.quoted[k])}
                  </td>
                ))}
                {['airfare', 'hotel', 'car', 'meals', 'misc', 'other', 'sold'].map((k) => (
                  <td key={k} style={{ ...td, fontSize: 11.5, textAlign: 'right' }}>
                    {money(shown.quoted[k])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}
